import { useEffect, useState } from 'react';
import { MapPin, Navigation, Music, Loader } from 'lucide-react';
import { useGeolocation } from '../hooks/useGeolocation';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase, AudioPin } from '../lib/supabase';
import { calculateDistance, formatDistance } from '../utils/distance';
import { YouTubePlayer } from './YouTubePlayer';

const PROXIMITY_RADIUS = 50;

export function NearbySoundDrops() {
  const { latitude, longitude, error, loading } = useGeolocation(true);
  const { t } = useLanguage();
  const [nearbyDrops, setNearbyDrops] = useState<(AudioPin & { distance: number })[]>([]);
  const [selectedDrop, setSelectedDrop] = useState<AudioPin | null>(null);
  const [loadingDrops, setLoadingDrops] = useState(false);

  useEffect(() => {
    if (!latitude || !longitude) return;

    const fetchNearbyDrops = async () => {
      setLoadingDrops(true);
      try {
        const { data, error: fetchError } = await supabase
          .from('audio_pins')
          .select(`
            *,
            profiles:user_id (
              username,
              display_name,
              avatar_url
            )
          `)
          .eq('is_public', true);

        if (fetchError) throw fetchError;

        if (data) {
          const dropsWithDistance = data
            .map((drop) => ({
              ...drop,
              distance: calculateDistance(
                latitude,
                longitude,
                drop.latitude,
                drop.longitude
              ),
            }))
            .filter((drop) => drop.distance <= PROXIMITY_RADIUS)
            .sort((a, b) => a.distance - b.distance);

          setNearbyDrops(dropsWithDistance);
        }
      } catch (err) {
        console.error('Error fetching nearby drops:', err);
      } finally {
        setLoadingDrops(false);
      }
    };

    fetchNearbyDrops();
    const interval = setInterval(fetchNearbyDrops, 10000);

    return () => clearInterval(interval);
  }, [latitude, longitude]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <Loader className="w-8 h-8 text-[#00c7b7] animate-spin mb-4" />
        <p className="text-gray-600">{t('nearby.searchingLocation')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <Navigation className="w-12 h-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {t('nearby.locationDisabled')}
        </h3>
        <p className="text-sm text-gray-600 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-[#00c7b7] text-white rounded-lg hover:bg-[#00c7b7] transition-colors"
        >
          {t('nearby.enableLocation')}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">{t('nearby.title')}</h2>
          {latitude && longitude && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Navigation className="w-4 h-4 text-[#00c7b7]" />
              <span className="font-mono">
                {latitude.toFixed(6)}, {longitude.toFixed(6)}
              </span>
            </div>
          )}
        </div>
        <p className="text-sm text-gray-600 mt-1">
          {t('nearby.within50m')} • {nearbyDrops.length} {nearbyDrops.length === 1 ? 'drop' : 'drops'}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {loadingDrops ? (
          <div className="flex items-center justify-center p-8">
            <Loader className="w-6 h-6 text-[#00c7b7] animate-spin" />
          </div>
        ) : nearbyDrops.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <MapPin className="w-16 h-16 text-gray-300 mb-4" />
            <p className="text-gray-600">{t('nearby.empty')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {nearbyDrops.map((drop) => (
              <div
                key={drop.id}
                className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setSelectedDrop(drop)}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                      <Music className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">{drop.title}</h3>
                    {drop.description && (
                      <p className="text-sm text-gray-600 line-clamp-2 mt-1">
                        {drop.description}
                      </p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Navigation className="w-3 h-3" />
                        {formatDistance(drop.distance)} {t('nearby.distance')}
                      </span>
                      {drop.profiles && (
                        <span>by @{drop.profiles.username}</span>
                      )}
                    </div>
                    {drop.tags && drop.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {drop.tags.map((tag, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedDrop && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900">{selectedDrop.title}</h2>
                  {selectedDrop.location_name && (
                    <p className="text-gray-600 mt-1">{selectedDrop.location_name}</p>
                  )}
                </div>
                <button
                  onClick={() => setSelectedDrop(null)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  ✕
                </button>
              </div>

              {selectedDrop.description && (
                <p className="text-gray-700 mb-4">{selectedDrop.description}</p>
              )}

              <YouTubePlayer
                videoId={selectedDrop.youtube_video_id}
                onPlayCountUpdate={() => {}}
              />

              {selectedDrop.tags && selectedDrop.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {selectedDrop.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span className="flex items-center gap-1">
                    <Navigation className="w-4 h-4" />
                    {formatDistance(
                      calculateDistance(
                        latitude!,
                        longitude!,
                        selectedDrop.latitude,
                        selectedDrop.longitude
                      )
                    )}{' '}
                    {t('nearby.distance')}
                  </span>
                  {selectedDrop.profiles && (
                    <span>by @{selectedDrop.profiles.username}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
